import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getQuoteApi, quotePdfUrl, type QuoteDetail } from "../lib/api.js";
import { Breadcrumbs } from "../ui/Breadcrumbs.js";
import { Badge } from "../ui/Badge.js";
import { LoadingBlock } from "../ui/Spinner.js";
import { EmptyState } from "../ui/EmptyState.js";
import { Button } from "../ui/Button.js";
import { formatCzk, formatDate } from "../lib/format.js";
import {
  DownloadIcon,
  DocumentIcon,
  ImageOffIcon,
  ChevronRightIcon,
} from "../lib/icons.js";

/**
 * /quote/:id — detail vytvořené nabídky s možností stáhnout PDF.
 */
export function QuoteDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<QuoteDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getQuoteApi(id)
      .then((d) => !cancelled && setData(d))
      .catch((err) => !cancelled && setError(String(err)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) return <LoadingBlock label="Načítám nabídku…" />;
  if (error)
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
          {error}
        </div>
      </div>
    );
  if (!data)
    return (
      <EmptyState title="Nabídka nenalezena" description="Zkontrolujte URL." />
    );

  const pdfUrl = id ? quotePdfUrl(id) : "#";

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <div className="mb-5">
        <Breadcrumbs
          items={[
            { label: "Nabídky", to: "/quotes" },
            { label: data.number },
          ]}
        />
      </div>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Badge tone="orange">{data.status}</Badge>
            <span className="text-sm text-zinc-500">
              Vytvořeno {formatDate(data.createdAt)}
            </span>
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-zinc-900">
            {data.number}
          </h1>
          {data.customerName && (
            <p className="mt-2 text-zinc-600">Zákazník: <strong className="text-zinc-900">{data.customerName}</strong></p>
          )}
        </div>

        <a href={pdfUrl} download>
          <Button iconLeft={<DownloadIcon size={16} />}>Stáhnout PDF</Button>
        </a>
      </div>

      {data.note && (
        <div className="mb-6 rounded-xl bg-zinc-50 border border-zinc-200 px-4 py-3 text-sm">
          <div className="text-[11px] uppercase tracking-wider text-zinc-400 font-medium mb-1">
            Poznámka
          </div>
          <div className="text-zinc-700">{data.note}</div>
        </div>
      )}

      <section className="bg-white rounded-2xl border border-zinc-200 shadow-card overflow-hidden">
        <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
          <h2 className="text-base font-semibold">Položky</h2>
          <span className="text-xs text-zinc-500">
            {data.items.length} {data.items.length === 1 ? "položka" : "položek"}
          </span>
        </div>
        <ul className="divide-y divide-zinc-100">
          {data.items.map((it) => (
            <li key={it.id} className="p-5 flex gap-4">
              <div className="w-16 h-16 bg-zinc-50 rounded-md flex items-center justify-center overflow-hidden flex-shrink-0 border border-zinc-100">
                {it.subgroup?.imageUrl ? (
                  <img
                    src={it.subgroup.imageUrl}
                    alt=""
                    className="max-w-full max-h-full object-contain p-1"
                  />
                ) : (
                  <ImageOffIcon size={20} className="text-zinc-300" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/part/${encodeURIComponent(it.articleNumber)}`}
                      className="text-sm font-medium text-zinc-900 hover:text-ktm-orange transition-colors line-clamp-2"
                    >
                      {it.articleName ?? it.articleNumber}
                    </Link>
                    <div className="mt-1 text-xs text-zinc-500 font-mono">
                      {it.articleNumber}
                      {it.lineNumber != null && (
                        <span className="text-zinc-400">  ·  poz. {it.lineNumber}</span>
                      )}
                    </div>
                    {(it.model || it.subgroup) && (
                      <div className="mt-1 text-[11px] text-zinc-400 flex items-center gap-1">
                        {it.model && (
                          <Link
                            to={`/model/${it.model.id}`}
                            className="hover:text-ktm-orange truncate"
                          >
                            {it.model.description}
                          </Link>
                        )}
                        {it.subgroup && (
                          <>
                            <ChevronRightIcon size={10} />
                            <span>{it.subgroup.description}</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold tabular-nums">
                      {formatCzk(parseFloat(it.unitPriceNetCzk) * it.quantity)}
                    </div>
                    <div className="text-[11px] text-zinc-400 tabular-nums">
                      {it.quantity} × {formatCzk(parseFloat(it.unitPriceNetCzk))}
                    </div>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>

        <div className="border-t border-zinc-100 px-5 py-4 bg-zinc-50 flex items-center justify-end">
          <div className="w-full max-w-sm space-y-1">
            <TotalRow label="Mezisoučet bez DPH" value={formatCzk(parseFloat(data.subtotalNetCzk))} />
            <TotalRow label="DPH 21 %" value={formatCzk(parseFloat(data.vatCzk))} />
            <div className="flex items-center justify-between pt-2 mt-1 border-t border-zinc-200">
              <span className="text-sm font-semibold">Celkem s DPH</span>
              <span className="text-xl font-semibold text-ktm-orange tabular-nums">
                {formatCzk(parseFloat(data.totalGrossCzk))}
              </span>
            </div>
          </div>
        </div>
      </section>

      <div className="mt-6 flex items-center gap-3">
        <a href={pdfUrl} download>
          <Button iconLeft={<DownloadIcon size={16} />}>Stáhnout PDF</Button>
        </a>
        <a href={pdfUrl} target="_blank" rel="noreferrer">
          <Button variant="secondary" iconLeft={<DocumentIcon size={16} />}>
            Otevřít v prohlížeči
          </Button>
        </a>
      </div>
    </div>
  );
}

function TotalRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-zinc-500">{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}
